import { Injectable, inject, signal } from '@angular/core';
import { Router }                    from '@angular/router';
import { BsModalService }            from 'ngx-bootstrap/modal';
import { forkJoin }                  from 'rxjs';
import { TranslateService }          from '@ngx-translate/core';

import { HttpService }                from 'src/app/services/http.service';
import { FonctionsService }           from 'src/app/services/fonctions-partagees.service';
import { SiteNavigationService }      from 'src/app/services/new_services/site-navigation.service';
import { LocalizedDatePipe }          from 'src/app/pipes/localized-date.pipe';
import { Projet }                     from 'src/app/bo/projet';
import { WorkflowModele }             from 'src/app/bo/workflowModele';
import { ProjetModaleComponent }      from 'src/app/components/projet-modale/projet-modale.component';
import { SiteReportsModaleComponent } from 'src/app/components/site-reports-modale/site-reports-modale.component';

export type SiteExportState = 'closed' | 'preparing' | 'empty';

/**
 * Pont de transition : actions « Projet », « Export KPIs » et « Report » de la page-bar du site,
 * reprises de MesProjetsComponent (/mysite/:id/myprojects), et « Modify » de la page-bar d'un projet,
 * reprise de ProjetDetailComponent, avec les mêmes modales et les mêmes appels API.
 * `old-theme` est posé sur la .modal-dialog pour que styles.scss s'applique à leur contenu.
 *
 * À supprimer quand ces modales auront été refondues.
 */
@Injectable({ providedIn: 'root' })
export class LegacySiteActionsService {

  private readonly http              = inject(HttpService);
  private readonly fonctionsService  = inject(FonctionsService);
  private readonly modalService      = inject(BsModalService);
  private readonly router            = inject(Router);
  private readonly siteNav           = inject(SiteNavigationService);
  private readonly translate         = inject(TranslateService);
  private readonly localizedDatePipe = inject(LocalizedDatePipe);

  readonly exportState = signal<SiteExportState>('closed');

  /** Reprise de MesProjetsComponent.ouvrirModaleAjout. */
  openCreateProject(): void {
    const siteId = this.siteNav.siteId();
    if (!siteId) return;
    this.fonctionsService.loading_screen(true);

    this.http.get<WorkflowModele[]>('Sites/' + siteId + '/WorkflowModeles').subscribe({
      next: workflows => {
        workflows.forEach(wkfw => wkfw.nomDate = wkfw.nom + ' (' + this.localizedDatePipe.transform(wkfw.dateDerniereModification) + ')');
        this.fonctionsService.loading_screen(false);
        if (!workflows.length) {
          this.translate.get('MES_PROJETS.MESSAGE_NO_WORKFLOW').subscribe(trad => {
            this.fonctionsService.launch_toast(false, false, trad);
          });
          return;
        }

        const modalRef = this.modalService.show(ProjetModaleComponent, {
          backdrop:            true,
          ignoreBackdropClick: true,
          keyboard:            false,
          class:               'old-theme',
          initialState: {
            workflows,
            estModif: false,
            siteId,
          } as Partial<ProjetModaleComponent>,
        });

        modalRef.onHidden?.subscribe(() => {
          const content = modalRef.content;
          if (content && !content.annul && content.success) this.siteNav.reloadBlasts();
        });
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.translate.get('GLOBAL.ERREUR_RECUP_DONNEES').subscribe(trad => {
          this.fonctionsService.launch_toast(false, false, trad);
        });
      },
    });
  }

  /**
   * Reprise de ProjetDetailComponent.ouvrirModaleModif. Après modification l'arbre est rechargé ;
   * après suppression on revient sur l'écran site (l'ancien front revenait à la liste des projets).
   */
  openModifyProject(projetId: number): void {
    const siteId = this.siteNav.siteId();
    if (!siteId) return;
    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);
    const stopLoader  = () => {
      clearTimeout(startLoader);
      this.fonctionsService.loading_screen(false);
    };

    forkJoin([
      this.http.get<Projet>('Projects/' + projetId),
      this.http.get<WorkflowModele[]>('Sites/' + siteId + '/WorkflowModeles'),
    ]).subscribe({
      next: ([projet, workflows]) => {
        stopLoader();
        if (projet.dateDebutPrevisionnelle) projet.dateDebutPrevisionnelle = new Date(projet.dateDebutPrevisionnelle);
        if (projet.dateFinPrevisionnelle)   projet.dateFinPrevisionnelle   = new Date(projet.dateFinPrevisionnelle);
        if (projet.dateDebutEffective)      projet.dateDebutEffective      = new Date(projet.dateDebutEffective);
        if (projet.dateFinEffective)        projet.dateFinEffective        = new Date(projet.dateFinEffective);
        workflows.forEach(wkfw => wkfw.nomDate = wkfw.nom + ' (' + wkfw.dateDerniereModification + ')');

        const modalRef = this.modalService.show(ProjetModaleComponent, {
          backdrop:            true,
          ignoreBackdropClick: true,
          keyboard:            false,
          class:               'old-theme',
          initialState: {
            estModif: true,
            projet,
            siteId,
            workflows,
          } as Partial<ProjetModaleComponent>,
        });

        modalRef.onHidden?.subscribe(() => {
          const content = modalRef.content;
          if (!content || content.annul || !content.success) return;
          this.siteNav.reloadBlasts();
          if (content.suppression) {
            this.router.navigate(['/site', siteId]);
          } else {
            this.router.navigate(['/site', siteId], {
              queryParams: { nodeValue: content.projet.nom },
              queryParamsHandling: 'merge',
            });
          }
        });
      },
      error: stopLoader,
    });
  }

  /** Reprise de MesProjetsComponent.lancerExport / exportDonneeAnalyse : CSV des données d'analyse du site. */
  exportKpis(): void {
    const siteId = this.siteNav.siteId();
    if (!siteId) return;
    this.fonctionsService.loading_screen(true);

    this.http.get<boolean>('Sites/' + siteId + '/GotAnalysisData').subscribe({
      next: gotData => {
        this.fonctionsService.loading_screen(false);
        if (!gotData) {
          this.exportState.set('empty');
          return;
        }
        this.exportState.set('preparing');
        this.http.downloadFile('Sites/' + siteId + '/ExportAnalysisData').subscribe({
          next: (resultat: any) => {
            if (!resultat) {
              this.exportState.set('closed');
              this.fonctionsService.launch_toast(false);
              return;
            }
            const blob = new Blob([resultat.body], { type: 'base64' });
            const a = document.createElement('a');
            a.href = window.URL.createObjectURL(blob);
            a.download = this.siteNav.siteName() + '.csv';
            document.body.appendChild(a);
            a.click();
            this.exportState.set('closed');
            setTimeout(() => a.remove(), 100);
          },
          error: () => {
            this.translate.get('GLOBAL.ERREUR_RECUP_DONNEES').subscribe(trad => {
              this.fonctionsService.launch_toast(false, false, trad);
            });
            this.exportState.set('closed');
          },
        });
      },
      error: () => this.fonctionsService.loading_screen(false),
    });
  }

  /** Reprise de MesProjetsComponent.openModalReports : la modale a besoin des projets du site avec leurs tirs. */
  openReports(): void {
    const siteId = this.siteNav.siteId();
    if (!siteId) return;
    this.fonctionsService.loading_screen(true);

    this.http.get<Projet[]>('Sites/' + siteId + '/Projects').subscribe({
      next: projets => {
        this.fonctionsService.loading_screen(false);
        this.modalService.show(SiteReportsModaleComponent, {
          class:        'old-theme',
          initialState: { siteId, tabProjets: projets } as Partial<SiteReportsModaleComponent>,
        });
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.translate.get('GLOBAL.ERREUR_RECUP_DONNEES').subscribe(trad => {
          this.fonctionsService.launch_toast(false, false, trad);
        });
      },
    });
  }
}
